/**
 * Партии — every party with an elected mayor, and what its administrations
 * commissioned.
 *
 * Grouped by party family by default, so a row is a party rather than a
 * ballot name. That matters more here than anywhere else: БСП has stood
 * under 24 names and half its procurement record is invisible from any one
 * of them. The grouping can be switched off to read the names as they stood
 * on the ballot, and narrowed to a single election.
 */

import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { DataTable, Pager } from '../components/DataTable';
import {
  Amount, Badge, ErrorState, Link, Loading, PageHead, SectionHead,
} from '../components/ui';
import { compact, num, truncate } from '../lib/format';
import { downloadXlsx, sheetOf, stampedFilename } from '../lib/xlsx';

const PAGE_SIZE = 25;

const PARTY_STATS = graphql(`
  query PartyStats($limit: Int!, $offset: Int!, $grouped: Boolean!, $electionYear: Int) {
    partyStats(limit: $limit, offset: $offset, grouped: $grouped, electionYear: $electionYear) {
      key
      name
      isFamily
      banners
      mayors
      municipalities
      councilSeats
      councils
      councilsLed
      councilsWithMajority
      currentMunicipalities
      awards
      value
      ministries
      governmentAwards
      governmentValue
    }
    partyStatCounts(grouped: $grouped) { value count }
    electionYears
  }
`);

export function PartiesPage() {
  const [grouped, setGrouped] = useState(true);
  const [year, setYear] = useState<number | null>(null);
  const [page, setPage] = useState(0);

  const query = useQuery({
    queryKey: ['party-stats', grouped, year, page],
    queryFn: () => request(PARTY_STATS, {
      limit: PAGE_SIZE, offset: page * PAGE_SIZE, grouped, electionYear: year,
    }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.partyStats ?? [];
  const perYear = Object.fromEntries(
    (query.data?.partyStatCounts ?? []).map((row) => [row.value, row.count]));
  const years = query.data?.electionYears ?? [];
  const total = year
    ? (perYear[String(year)] ?? 0)
    : Object.values(perYear).reduce((most, n) => Math.max(most, n), 0);

  /* Procurement data begins in 2020, so a mandate that ended before then has
     no awards to attribute — ГЕРБ's 2015 row reads 0 лв, which is the truth
     and looks like a fault. Said plainly rather than left to be puzzled over. */
  const earliestAward = 2020;
  const mandateEndsBeforeArchive = year != null
    && years.length > 0
    && Math.min(...years.filter((option) => option > year), 9999) <= earliestAward;

  const reset = () => { setGrouped(true); setYear(null); setPage(0); };
  const active = (grouped ? 0 : 1) + (year ? 1 : 0);

  /* The whole filtered ranking, not the visible page: the pager exists for
     reading, the file is for working. The list is a few hundred rows at
     most, so it is walked in a handful of requests. */
  const [exporting, setExporting] = useState(false);
  /* A failed export used to re-enable the button and say nothing,
     which reads as a file that silently did not download. */
  const [exportError, setExportError] = useState<string | null>(null);
  const exportAll = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const all: typeof rows = [];
      const batch = 200;
      for (let offset = 0; offset < 10_000; offset += batch) {
        const data = await request(PARTY_STATS, {
          limit: batch, offset, grouped, electionYear: year,
        });
        all.push(...data.partyStats);
        if (data.partyStats.length < batch) break;
      }
      downloadXlsx(stampedFilename('партии'), [
        sheetOf('Партии', [
          { header: 'Партия', value: (row: typeof rows[number]) => row.name },
          { header: 'Обединена по партия', value: (row) => (row.isFamily ? 'да' : 'не') },
          { header: 'Наименования на бюлетината', value: (row) => row.banners },
          { header: 'Кметове', value: (row) => row.mayors },
          { header: 'Общини общо', value: (row) => row.municipalities },
          { header: 'Общини в мандат', value: (row) => row.currentMunicipalities },
          { header: 'Поръчки', value: (row) => row.awards },
          { header: 'Стойност (лв.)', format: 'money', value: (row) => row.value },
        ], all),
        {
          name: 'Справка',
          header: ['Поле', 'Стойност'],
          rows: [
            ['Източник', 'Алфа-Омега — от бюджета до получателя'],
            ['Изтеглено на', new Date().toISOString()],
            ['Групиране', grouped ? 'обединени по партия' : 'както са на бюлетината'],
            ['Местни избори', year ?? 'всички заредени'],
            ['Редове', all.length],
          ],
        },
      ]);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHead
        eyebrow="Политически субекти"
        title="Партии"
        lede={'Партии и местни формации с избран кмет, подредени по стойността на '
          + 'поръчките, възложени от техните администрации. Поръчката се приписва на '
          + 'партията, управлявала към датата на възлагане, а не на днешното управление.'}
        actions={(
          <>
          <button
            type="button"
            className="btn"
            onClick={() => { void exportAll(); }}
            disabled={exporting || query.isPending}
            title="Изтегля целия списък при избраните групиране и избори"
          >
            {exporting ? 'Изтегляне…' : 'Експорт XLSX'}
          </button>
          {exportError && (
            <span className="muted">Експортът не сполучи: {exportError}</span>
          )}
          </>
        )}
      />

      <div className="list-shell">
        {/* The same controls as the other lists, in the same place. Grouping
            is the one that changes the reading most, so it is first. */}
        <aside className="facet-panel">
          <div className="facet-panel-head">
            <span className="facet-panel-title">Филтри</span>
            {active > 0 && (
              <button type="button" className="facet-reset" onClick={reset}>
                изчисти ({active})
              </button>
            )}
          </div>

          <section className="fct-group">
            <div className="fct-group-head"><span>Групиране</span></div>
            <div className="fct-rows">
              <label className="fct-row">
                <span className="fct-spacer" />
                <input type="radio" checked={grouped}
                  onChange={() => { setGrouped(true); setPage(0); }} />
                <span className="fct-name">Обединени по партия</span>
                <span className="fct-count" />
              </label>
              <label className="fct-row">
                <span className="fct-spacer" />
                <input type="radio" checked={!grouped}
                  onChange={() => { setGrouped(false); setPage(0); }} />
                <span className="fct-name">Както са на бюлетината</span>
                <span className="fct-count" />
              </label>
              <span className="fct-empty">
                Партия се явява на избори под различни наименования. Обединяването е
                ръчно изготвено и не обхваща местните формации и независимите
                кандидати — те стоят самостойно.
              </span>
            </div>
          </section>

          <section className="fct-group">
            <div className="fct-group-head"><span>Местни избори</span></div>
            <div className="fct-rows">
              <label className="fct-row">
                <span className="fct-spacer" />
                <input type="radio" checked={year == null}
                  onChange={() => { setYear(null); setPage(0); }} />
                <span className="fct-name">всички</span>
                <span className="fct-count" />
              </label>
              {years.map((option) => (
                <label className="fct-row" key={option}>
                  <span className="fct-spacer" />
                  <input type="radio" checked={year === option}
                    onChange={() => { setYear(option); setPage(0); }} />
                  <span className="fct-name">{`${option} г.`}</span>
                  <span className="fct-count">{num(perYear[String(option)] ?? 0)}</span>
                </label>
              ))}
            </div>
          </section>
        </aside>

        <div>
          {mandateEndsBeforeArchive && (
            <p className="page-lede" style={{ marginBottom: '1rem' }}>
              <Badge kind="record">без данни за поръчки</Badge>{' '}
              {`Мандатите от ${year} г. приключват преди ${earliestAward} г., откогато `}
              започват данните за обществените поръчки в ЦАИС ЕОП. Стойностите за този
              избор са нула, защото няма възложени поръчки в периода на мандата — не
              защото такива не са били възлагани.
            </p>
          )}

          {query.isError && <ErrorState message={(query.error).message} />}
          {query.isPending ? <Loading /> : (
            <section className="block">
              <SectionHead
                title={year ? `Местни избори ${year} г.` : 'Всички заредени избори'}
                aside={grouped ? 'обединени по партия' : 'по наименование на бюлетината'}
              />
              <DataTable
                rows={rows}
                rowKey={(row) => row.key}
                empty="Няма партии за тези условия."
                columns={[
                  {
                    key: 'name',
                    label: 'Партия',
                    render: (row) => (
                      <div>
                        {/* The badge sits with the name rather than in a
                            column of its own: a whole column to carry one
                            short label pushed the table into horizontal
                            scrolling, and it says something about the name
                            rather than about the party. */}
                        <span className="party-name">
                          <Link href={`#/parties/${encodeURIComponent(row.key)}`}>
                            {truncate(row.name, 46)}
                          </Link>
                          {row.isFamily && <Badge kind="party">обединена</Badge>}
                        </span>
                        <div className="uic mono">
                          {row.isFamily
                            ? `${row.banners} наименования на бюлетината`
                            : 'самостойна формация'}
                        </div>
                      </div>
                    ),
                  },
                  {
                    /* Seven columns became four. Mayors, municipalities,
                       councils, seats, councils led and councils held were
                       six figures in five columns, each too narrow for its
                       own heading — the party name wrapped over four lines
                       while „ВОДИ · МНОЗИНСТВО" sat beside it, and the
                       table still ran off the right edge.

                       They are one thing — how much local power a party
                       holds — so they are one cell, as labelled rows whose
                       labels line up across every party. The eye compares
                       down a column as before; the table no longer scrolls
                       sideways to allow it. */
                    key: 'local',
                    label: 'Местна власт',
                    render: (row) => (
                      <div className="stat-rows">
                        <div className="stat-row">
                          <span className="stat-row-key">кметове</span>
                          <span className="stat-row-value">{num(row.mayors)}</span>
                          <span className="stat-row-note">
                            {row.currentMunicipalities === row.municipalities
                              ? `${num(row.municipalities)} общини`
                              : `${num(row.currentMunicipalities)} от ${num(row.municipalities)} общини`}
                          </span>
                        </div>
                        <div className="stat-row">
                          <span className="stat-row-key">съвети</span>
                          <span className="stat-row-value">{num(row.councils)}</span>
                          <span className="stat-row-note">
                            {row.councilSeats > 0
                              ? `${num(row.councilSeats)} съветници`
                              : 'без съветници'}
                          </span>
                        </div>
                        {/* Leading is not controlling, and the gap between
                            the two is the point: ГЕРБ leads 131 councils
                            and can carry a vote unaided in 47. */}
                        <div className="stat-row">
                          <span className="stat-row-key">мнозинство</span>
                          <span className="stat-row-value">
                            {num(row.councilsWithMajority)}
                          </span>
                          <span className="stat-row-note">
                            {row.councilsLed > 0
                              ? `водеща в ${num(row.councilsLed)}`
                              : 'не води съвет'}
                          </span>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'value',
                    label: 'Общински поръчки',
                    num: true,
                    render: (row) => (
                      <div>
                        <Amount value={row.value} />
                        <div className="uic mono">
                          {num(row.awards)}
                          {row.municipalities > 0
                            ? ` · ${compact(row.value / Math.max(1, row.municipalities))} лв / община`
                            : ''}
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'governmentValue',
                    label: 'Държавни поръчки',
                    num: true,
                    render: (row) => (row.ministries > 0
                      ? (
                        <div>
                          <Amount value={row.governmentValue} />
                          <div className="uic mono">
                            {`${num(row.governmentAwards)} поръчки · ${num(row.ministries)} министерства`}
                          </div>
                        </div>
                      )
                      // Not zero: the party never held a ministry we can
                      // attribute, which is a different fact from having
                      // held one and spent nothing.
                      : <span className="muted">—</span>),
                  },
                ]}
              />
              <Pager page={page} pageSize={PAGE_SIZE} total={total} onGo={setPage} />
            </section>
          )}

          <p className="colophon-note">
            „Кметове“ брои отделните лица, избрани за кмет, а не мандатите — кмет,
            преизбран три пъти, се брои веднъж. Стойността обхваща поръчки, възложени от
            общините с кмет от тази партия в периода на съответния мандат;
            министерствата и другите държавни възложители не се приписват на партия,
            защото се съхраняват като име върху поръчката, а не като отделен обект.
          </p>

          <p className="colophon-note">
            Стойността се приписва по партията на кмета, защото той е възложителят,
            подписал договора. Това не значи, че партията е управлявала сама: бюджетът и
            разходите се приемат от общинския съвет, а в 161 от 265 общини партията на
            кмета няма мнозинство в него — в 52 съветът се води от друга партия.
            Затова колоните „Съвети“ и „Води · мнозинство“ стоят до „Кметове“:
            заедно казват дали една партия е управлявала, или се е договаряла.
            Сумите умишлено не се разпределят между партиите според броя места —
            делът на една група в съвета не е делът ѝ в един договор, а кой как е
            гласувал по конкретен бюджет не се публикува машинночетимо никъде.
          </p>
        </div>
      </div>
    </>
  );
}
